import React, { useState, useRef, useCallback } from 'react';
import { AlertTriangle, X, Trash2, CheckCircle, Info } from 'lucide-react';

/**
 * ConfirmModal - A styled confirmation/alert/prompt dialog to replace native browser dialogs
 * 
 * Usage Examples:
 * 
 * // Confirmation dialog (replaces window.confirm)
 * setConfirmModal({ 
 *   isOpen: true, 
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?',
 *   confirmText: 'Delete',
 *   type: 'danger',
 *   onConfirm: () => { deleteItem(); }
 * });
 * 
 * // Alert/Info dialog (replaces alert()) - single button
 * setConfirmModal({
 *   isOpen: true,
 *   title: 'Success',
 *   message: 'Item was saved successfully!',
 *   type: 'success',
 *   singleButton: true,
 *   confirmText: 'OK',
 *   onConfirm: () => {}
 * });
 * 
 * // Prompt dialog (replaces prompt()) - with input field
 * setConfirmModal({
 *   isOpen: true,
 *   title: 'Enter Reason',
 *   message: 'Please provide a reason for rejection:',
 *   type: 'warning',
 *   showInput: true,
 *   inputPlaceholder: 'Enter reason...',
 *   confirmText: 'Submit',
 *   onConfirm: (inputValue) => { handleRejection(inputValue); }
 * });
 */

// Inner component that handles input state - remounts when modal opens
const ModalContent = ({ 
  onClose, 
  onConfirm, 
  title,
  message,
  confirmText,
  cancelText,
  type,
  singleButton,
  showInput,
  inputPlaceholder,
  inputDefaultValue,
  style
}) => {
  const [inputValue, setInputValue] = useState(inputDefaultValue);
  const inputRef = useRef(null);

  const handleConfirm = useCallback(() => {
    if (showInput) {
      onConfirm?.(inputValue);
    } else {
      onConfirm?.();
    }
  }, [showInput, inputValue, onConfirm]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && showInput) {
      handleConfirm();
    }
  }, [showInput, handleConfirm]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
      {/* Header with icon */}
      <div className="p-6 pb-4">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-full ${style.iconBg}`}>
            {style.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {message}
            </p>
            
            {/* Input field for prompt mode */}
            {showInput && (
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                className="mt-3 w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
                autoFocus
              />
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className={`px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex ${singleButton ? 'justify-center' : 'justify-end'} gap-3`}>
        {!singleButton && (
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {cancelText}
          </button>
        )}
        <button
          onClick={handleConfirm}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${style.confirmBtn}`}
        >
          {confirmText}
        </button>
      </div>
    </div>
  );
};

const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger', // 'danger' | 'warning' | 'info' | 'success'
  singleButton = false, // If true, only show confirm button (for alerts)
  showInput = false, // If true, show input field (for prompts)
  inputPlaceholder = 'Enter value...',
  inputDefaultValue = ''
}) => {
  if (!isOpen) return null;

  const typeStyles = {
    danger: {
      icon: <Trash2 className="h-6 w-6 text-red-500" />,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      confirmBtn: 'bg-red-500 hover:bg-red-600 text-white'
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-amber-500" />,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      confirmBtn: 'bg-amber-500 hover:bg-amber-600 text-white'
    },
    info: {
      icon: <Info className="h-6 w-6 text-blue-500" />,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      confirmBtn: 'bg-blue-500 hover:bg-blue-600 text-white'
    },
    success: {
      icon: <CheckCircle className="h-6 w-6 text-emerald-500" />,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      confirmBtn: 'bg-emerald-500 hover:bg-emerald-600 text-white'
    }
  };

  const style = typeStyles[type] || typeStyles.danger;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[100] bg-black/50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <ModalContent
          key={`modal-${title}-${message}`}  
          onClose={onClose}
          onConfirm={onConfirm}
          title={title}
          message={message}
          confirmText={confirmText}
          cancelText={cancelText}
          type={type}
          singleButton={singleButton}
          showInput={showInput}
          inputPlaceholder={inputPlaceholder}
          inputDefaultValue={inputDefaultValue}
          style={style}
        />
      </div>
    </>
  );
};

export default ConfirmModal;
